import { ProjectData } from "../types/index";

export const projectData: ProjectData = {
  projects: [
    {
      key: "tabby",
      name: "tabby",
      tag: "Electron · React",
      year: "2026",
      blurb:
        "A desktop sticky-notes app that lives on top of every window. Built because I kept losing tabs full of half-written reminders.",
      status: "shipping",
      shape: "circle",
      role: "sole maker",
      timeframe: "Jan 2026 — ongoing",
      stack: ["Electron", "React", "TypeScript", "IndexedDB"],
      links: [
        { label: "live", href: "tabby.miceha.codes" },
        { label: "source", href: "github.com/miceha/tabby" },
      ],
      story: [
        "tabby is a desktop sticky-notes app that lives on top of every window I have open. I built it because I kept losing tabs full of half-written reminders to myself — the kind that aren't important enough for a todo app, but that I'd want to glance at twenty times a day.",
        "It's an Electron app, which is the obvious wrong technical choice and also the right one. I wanted to ship something that ran on my actual machine, in less than a month, and React-on-Electron is the path of least resistance for someone who already knows React. The bundle is heavy. The notes are fast.",
        "The interesting parts were the bits that aren't really frontend: window-level always-on-top behaviour, persisting state in IndexedDB so notes survive a restart, and the global keyboard shortcut that summons the app from anywhere. None of these were hard, exactly. They were just unfamiliar.",
        "Things I'd do differently: I'd start with Tauri instead of Electron, mostly out of curiosity. I'd use a real key-value store instead of IndexedDB, which felt like overkill for what is essentially a JSON file. And I'd resist the temptation to add tags until I'd used the untagged version for a month.",
      ],
      learned: [
        "How to ship something that isn't just a webpage.",
        "Electron's main/renderer split, and why it exists.",
        "IndexedDB is too much database for a sticky-notes app.",
        "Global keyboard shortcuts are surprisingly fiddly across platforms.",
      ],
    },
    {
      key: "syntax-garden",
      name: "syntax-garden",
      tag: "Next.js · TypeScript",
      year: "2025",
      blurb:
        "A tag-able collection for the code snippets I keep losing in Slack DMs. Now indexed, searchable, and quietly hosted.",
      status: "live",
      shape: "squircle",
      role: "sole maker",
      timeframe: "Sep — Dec 2025",
      stack: ["Next.js", "TypeScript", "MDX", "Vercel"],
      links: [
        { label: "live", href: "snippets.miceha.codes" },
        { label: "source", href: "github.com/miceha/syntax-garden" },
      ],
      story: [
        "Syntax-garden is a small, tag-able collection of code snippets I keep referring back to. It started as a folder of .md files on my desktop and turned into a Next.js app the weekend I got tired of grepping through it.",
        "There's nothing groundbreaking here. It's an MDX site with a tags index, a search box, and a colour palette I'm fond of. The interesting thing for me was learning how to lay out a content-driven app — the routing, the build-time indexing, the small ergonomic affordances that make a site feel cared-for.",
        "I use it almost every day, which I think is the strongest endorsement I can give my own work.",
      ],
      learned: [
        "Next.js app router from scratch.",
        "How to build a content index at build time without an external service.",
        "How much I like writing in MDX.",
      ],
    },
    {
      key: "feedwave",
      name: "feedwave",
      tag: "Next.js · Postgres",
      year: "2025",
      blurb:
        'A small, opinionated RSS reader. No algorithm, no comments, no "for you". Just the boring web, in order.',
      status: "beta",
      shape: "pill",
      role: "sole maker",
      timeframe: "Jun — Aug 2025",
      stack: ["Next.js", "Postgres", "Drizzle", "Vercel"],
      links: [
        { label: "live", href: "feedwave.miceha.codes" },
        { label: "source", href: "github.com/miceha/feedwave" },
      ],
      story: [
        'Feedwave is an RSS reader, but small and slightly opinionated. No algorithm, no comments, no "for you" feed. You add a URL, and you get the posts in the order they were published. That\'s the whole product.',
        "I built it because the RSS readers I tried either had ten features I didn't want, or were so spartan that I couldn't tell which feeds had new posts. Feedwave is the middle: just enough chrome to be pleasant, nothing more.",
        "This is the project that pushed me into the backend. I'd been getting away with serverless functions and KV stores, but this one needed a real database — feeds, items, read state, the joins between them. So I picked Postgres, wired up Drizzle, and learned which of my SQL instincts were correct.",
      ],
      learned: [
        "Postgres basics: schemas, joins, indexes.",
        "How to design a small relational model without overthinking it.",
        "RSS is a much more pleasant protocol than I assumed.",
        "Background jobs are a different category of problem than I'd appreciated.",
      ],
    },
    {
      key: "thirtyone",
      name: "thirtyone",
      tag: "React · IndexedDB",
      year: "2024",
      blurb:
        "An offline-first habit tracker that doesn't want your email address. Local-only by design.",
      status: "archived",
      shape: "blob",
      role: "sole maker",
      timeframe: "Nov 2024",
      stack: ["React", "IndexedDB", "Vite"],
      links: [
        { label: "live", href: "thirtyone.miceha.codes" },
        { label: "source", href: "github.com/miceha/thirtyone" },
      ],
      story: [
        "Thirtyone is a habit tracker I built in a weekend after deleting three of them off my phone. It doesn't ask for your email. It doesn't ask for anything. It stores your habits in IndexedDB and that's the end of the story.",
        "I haven't touched it in over a year, which feels right. Some software is meant to be finished.",
      ],
      learned: [
        "Sometimes the best feature is the one you didn't add.",
        "IndexedDB through a thin wrapper is genuinely fine.",
      ],
    },
  ],
};
